import { useState, useEffect, useCallback } from "react";
import { MODULOS_EDITABLES } from "./permisos";

const C = {
  red:      "#E30613",
  redDark:  "#B0000E",
  navy:     "#0f172a",
  gray:     "#1e293b",
  slate:    "#64748b",
  light:    "#94a3b8",
  white:    "#ffffff",
  green:    "#15803d",
  greenBg:  "#f0fdf4",
  greenBorder: "#86efac",
};

const ESTADO_COLORS = {
  pendiente:  { bg: "#fefce8", color: "#854d0e", border: "#fde047" },
  aprobado:   { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
  rechazado:  { bg: "#fef2f2", color: "#991b1b", border: "#fca5a5" },
};

const ROL_COLORS = {
  "Administrador":       { bg: "#fef2f2", color: "#991b1b" },
  "Gerente/Jefe General":{ bg: "#eff6ff", color: "#1d4ed8" },
  "Jefe de Zona":        { bg: "#f0fdf4", color: "#15803d" },
  "Directo Claro":       { bg: "#fdf4ff", color: "#7e22ce" },
  "Owner":               { bg: "#fff7ed", color: "#c2410c" },
};

// Selector de permisos compartido por el modal de "Aprobar usuario" y el
// modal de "Permisos" (usuario ya aprobado). Tres opciones en forma de
// tarjetas, igual al estilo que ya tenía el modal de aprobar, más una
// casilla por módulo cuando se elige la tercera opción.
function SelectorPermisoModulos({ modo, setModo, modulosSeleccionados, onToggleModulo }) {
  const opciones = [
    { key: "visualizador",   emoji: "👁️", titulo: "Solo visualización", desc: "Puede ver toda la información pero no modificar nada", color: "#3b82f6", bg: "#eff6ff" },
    { key: "editor_todo",    emoji: "✏️", titulo: "Puede editar en todo", desc: "Acceso de edición en todos los módulos (como antes)", color: "#15803d", bg: "#f0fdf4" },
    { key: "editor_modulos", emoji: "🎛️", titulo: "Editor solo en módulos específicos", desc: "Elige abajo en cuáles secciones puede editar; en las demás solo ve", color: "#c2410c", bg: "#fff7ed" },
  ];
  return (
    <div>
      {opciones.map((op) => (
        <div key={op.key}
          onClick={() => setModo(op.key)}
          style={{
            border: `2px solid ${modo === op.key ? op.color : "#e2e8f0"}`,
            borderRadius: "10px", padding: "14px 16px", cursor: "pointer", marginBottom: "8px",
            background: modo === op.key ? op.bg : "white",
            transition: "all 0.15s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: `2px solid ${modo === op.key ? op.color : "#cbd5e1"}`, background: modo === op.key ? op.color : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {modo === op.key && <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "white" }} />}
            </div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#0f172a" }}>{op.emoji} {op.titulo}</div>
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{op.desc}</div>
            </div>
          </div>

          {op.key === "editor_modulos" && modo === "editor_modulos" && (
            <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #fed7aa", display: "flex", flexDirection: "column", gap: "8px" }}
              onClick={(e) => e.stopPropagation()}>
              {MODULOS_EDITABLES.map((m) => {
                const marcado = modulosSeleccionados.includes(m.key);
                return (
                  <label key={m.key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: "#1e293b", cursor: "pointer" }}>
                    <input type="checkbox" checked={marcado} onChange={() => onToggleModulo(m.key)}
                      style={{ width: "15px", height: "15px", accentColor: "#c2410c", cursor: "pointer" }} />
                    {m.label}
                  </label>
                );
              })}
              {modulosSeleccionados.length === 0 && (
                <div style={{ fontSize: "11px", color: "#9a3412", fontStyle: "italic" }}>
                  Sin ningún módulo marcado, este usuario no podrá editar nada.
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function GestionUsuarios() {
  const [tab, setTab] = useState("usuarios"); // "usuarios" | "activos" | "auditoria" | "historial"
  const [usuarios, setUsuarios]   = useState([]);
  const [filtro, setFiltro]       = useState("pendiente");
  const [loading, setLoading]     = useState(true);
  const [mensaje, setMensaje]     = useState("");
  const [tipoMsg, setTipoMsg]     = useState("");
  const [procesando, setProcesando] = useState(null);
  // Modal de aprobación con selector de permisos
  const [modalAprobar, setModalAprobar] = useState(null); // { id, nombre }
  // Modal de permisos (para usuarios ya aprobados) — elegir, módulo por
  // módulo, en cuáles puede editar y en cuáles solo puede ver.
  const [modalPermisos, setModalPermisos] = useState(null); // { id, nombre }
  // Estado compartido por AMBOS modales (Aprobar y Permisos):
  //   "visualizador"    → no edita nada
  //   "editor_todo"     → edita en todos los módulos (igual que siempre)
  //   "editor_modulos"  → edita SOLO en los módulos marcados abajo
  const [modoPermiso, setModoPermiso] = useState("visualizador");
  const [modulosSeleccionados, setModulosSeleccionados] = useState([]);
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);
  // Modal reset contraseña
  const [modalReset, setModalReset] = useState(null); // { id, nombre }
  const [passReset, setPassReset]   = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetExito, setResetExito] = useState("");

  // Sesiones activas
  const [sesiones, setSesiones]       = useState([]);
  const [loadingSes, setLoadingSes]   = useState(false);

  // Auditoría
  const [auditoria, setAuditoria]     = useState([]);
  const [loadingAud, setLoadingAud]   = useState(false);
  const [filtroCedAud, setFiltroCedAud] = useState("");

  // ── NUEVO: Historial de visitas ──
  const [historial, setHistorial]       = useState([]);
  const [loadingHist, setLoadingHist]   = useState(false);
  const [histFiltros, setHistFiltros]   = useState({ cedula: "", desde: "", hasta: "" });

  const cargarUsuarios = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/usuarios`)
      .then(res => res.json())
      .then(data => {
        // Normalizar puede_editar a booleano real (el driver MySQL puede retornar 0/1 o "0"/"1")
        const normalizado = data.map(u => ({ ...u, puede_editar: u.puede_editar === 1 || u.puede_editar === true || u.puede_editar === "1" }));
        setUsuarios(normalizado);
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  };

  const cargarSesiones = useCallback(() => {
    setLoadingSes(true);
    fetch(`${process.env.REACT_APP_API_URL}/sesiones-activas`)
      .then(res => res.json())
      .then(data => { setSesiones(data); setLoadingSes(false); })
      .catch(() => setLoadingSes(false));
  }, []);

  const cargarAuditoria = useCallback(() => {
    setLoadingAud(true);
    const url = filtroCedAud
      ? `${process.env.REACT_APP_API_URL}/auditoria?cedula=${filtroCedAud}&limite=200`
      : `${process.env.REACT_APP_API_URL}/auditoria?limite=200`;
    fetch(url)
      .then(res => res.json())
      .then(data => { setAuditoria(data); setLoadingAud(false); })
      .catch(() => setLoadingAud(false));
  }, [filtroCedAud]);

  // ── NUEVO: cargar historial de visitas con filtros ──
  const cargarHistorial = useCallback(() => {
    setLoadingHist(true);
    const params = new URLSearchParams({ limite: 500 });
    if (histFiltros.cedula) params.append("cedula", histFiltros.cedula);
    if (histFiltros.desde)  params.append("desde",  histFiltros.desde);
    if (histFiltros.hasta)  params.append("hasta",  histFiltros.hasta);
    fetch(`${process.env.REACT_APP_API_URL}/historial-visitas?${params}`)
      .then(r => r.json())
      .then(data => { setHistorial(data); setLoadingHist(false); })
      .catch(() => setLoadingHist(false));
  }, [histFiltros]);

  useEffect(() => { cargarUsuarios(); }, []);

  // Auto-refresh sesiones activas cada 30s cuando esa tab está activa
  useEffect(() => {
    if (tab !== "activos") return;
    cargarSesiones();
    const iv = setInterval(cargarSesiones, 30000);
    return () => clearInterval(iv);
  }, [tab, cargarSesiones]);

  useEffect(() => {
    if (tab === "auditoria") cargarAuditoria();
  }, [tab, cargarAuditoria]);

  // ── NUEVO: cargar historial cuando se abre esa tab ──
  useEffect(() => {
    if (tab === "historial") cargarHistorial();
  }, [tab, cargarHistorial]);

  const mostrarMensaje = (texto, tipo) => {
    setMensaje(texto); setTipoMsg(tipo);
    setTimeout(() => setMensaje(""), 4000);
  };

  const aprobar = (id, nombre) => {
    // Abrir modal para elegir permisos antes de aprobar
    setModalAprobar({ id, nombre });
    setModoPermiso("visualizador");
    setModulosSeleccionados([]);
  };

  // Construye el body { puede_editar, modulos_edicion } a partir del modo
  // elegido en el modal — lo comparten Aprobar y Permisos.
  const cuerpoPermisoDesdeModo = () => {
    if (modoPermiso === "editor_modulos") {
      return { puede_editar: 1, modulos_edicion: modulosSeleccionados };
    }
    // "visualizador" / "editor_todo" → modo de siempre (todo o nada),
    // modulos_edicion = null para no dejar una restricción vieja pegada.
    return { puede_editar: modoPermiso === "editor_todo" ? 1 : 0, modulos_edicion: null };
  };

  const confirmarAprobacion = async () => {
    if (!modalAprobar) return;
    const { id } = modalAprobar;
    setProcesando(id);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/usuarios/${id}/aprobar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cuerpoPermisoDesdeModo()),
      });
      const data = await res.json();
      if (data.ok) { mostrarMensaje("✅ Usuario aprobado correctamente.", "ok"); cargarUsuarios(); }
      else mostrarMensaje("❌ Error al aprobar usuario.", "error");
    } catch { mostrarMensaje("❌ No se pudo conectar.", "error"); }
    finally { setProcesando(null); setModalAprobar(null); }
  };

  // Abre el modal de permisos para un usuario YA aprobado, precargando
  // su estado actual (visualizador / editor todo / editor por módulos).
  const abrirModalPermisos = (u) => {
    setModalPermisos({ id: u.id, nombre: `${u.nombre} ${u.apellido}` });
    if (Array.isArray(u.modulos_edicion)) {
      setModoPermiso("editor_modulos");
      setModulosSeleccionados(u.modulos_edicion);
    } else {
      setModoPermiso(u.puede_editar ? "editor_todo" : "visualizador");
      setModulosSeleccionados([]);
    }
  };

  const confirmarPermisos = async () => {
    if (!modalPermisos) return;
    setGuardandoPermisos(true);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/usuarios/${modalPermisos.id}/permiso`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cuerpoPermisoDesdeModo()),
      });
      const data = await res.json();
      if (data.ok) { mostrarMensaje("✅ Permisos actualizados correctamente.", "ok"); cargarUsuarios(); setModalPermisos(null); }
      else mostrarMensaje("❌ Error al actualizar permisos.", "error");
    } catch { mostrarMensaje("❌ No se pudo conectar.", "error"); }
    finally { setGuardandoPermisos(false); }
  };

  const alternarModuloSeleccionado = (key) => {
    setModulosSeleccionados((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const abrirModalReset = (id, nombre) => {
    setModalReset({ id, nombre });
    setPassReset(""); setResetError(""); setResetExito("");
  };

  const confirmarReset = async () => {
    if (!modalReset) return;
    if (passReset.length < 6) { setResetError("Mínimo 6 caracteres."); return; }
    setResetLoading(true); setResetError(""); setResetExito("");
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/usuarios/${modalReset.id}/reset-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nueva_password: passReset }),
      });
      const data = await res.json();
      if (data.ok) {
        setResetExito("✅ Contraseña actualizada correctamente.");
        setTimeout(() => { setModalReset(null); setResetExito(""); }, 1800);
      } else setResetError(data.mensaje || "Error al resetear.");
    } catch { setResetError("No se pudo conectar."); }
    finally { setResetLoading(false); }
  };

  const rechazar = async (id) => {
    if (!window.confirm("¿Seguro que deseas rechazar este usuario?")) return;
    setProcesando(id);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/usuarios/${id}/rechazar`, { method: "PUT" });
      const data = await res.json();
      if (data.ok) { mostrarMensaje("Usuario rechazado.", "ok"); cargarUsuarios(); }
      else mostrarMensaje("❌ Error al rechazar usuario.", "error");
    } catch { mostrarMensaje("❌ No se pudo conectar.", "error"); }
    finally { setProcesando(null); }
  };

  const eliminarUsuario = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar definitivamente el registro de "${nombre}"? Esta acción no se puede deshacer — la cédula, el teléfono y el correo quedarán libres para un nuevo registro.`)) return;
    setProcesando(id);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/usuarios/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.ok) { mostrarMensaje("🗑️ Usuario eliminado.", "ok"); cargarUsuarios(); }
      else mostrarMensaje(data.mensaje || "❌ Error al eliminar usuario.", "error");
    } catch { mostrarMensaje("❌ No se pudo conectar.", "error"); }
    finally { setProcesando(null); }
  };

  const usuariosFiltrados = usuarios.filter(u => filtro === "todos" ? true : u.estado === filtro);
  const pendientes = usuarios.filter(u => u.estado === "pendiente").length;

  const thStyle = {
    padding: "10px 12px", background: C.navy, color: "white",
    textAlign: "left", fontSize: "11px", fontWeight: "600",
    textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
  };
  const tdStyle = {
    padding: "10px 12px", fontSize: "13px", color: C.navy,
    borderBottom: "1px solid #f1f5f9", verticalAlign: "middle",
  };

  const formatFecha = (f) => {
    if (!f) return "—";
    const d = new Date(f);
    return d.toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" });
  };

  const paginaLabel = (p) => {
    const mapa = {
      "/":                    "🏠 Dashboard",
      "/ofensores-n2":        "📡 Ofensores N2+",
      "/resumen-icr":         "📊 Resumen ICR",
      "/causas-falla":        "⚡ Causas Falla",
      "/reportes":            "📋 Reportes",
      "/semaforo":            "🚦 Semáforo",
      "/planes":              "📝 Planes",
      "/usuarios":            "👥 Usuarios",
      "/cargar":              "⬆️ Cargar",
      "/priorizacion-huella": "🗺️ Priorización Huella",
      "/gestion-huella":      "⚙️ Gestión Huella",
    };
    return mapa[p] || p || "—";
  };

  return (
    <div style={{ padding: "4px 0" }}>

      {/* Título */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <div style={{ width: "4px", height: "24px", background: C.red, borderRadius: "2px" }} />
        <h2 style={{ margin: 0, color: C.navy, fontSize: "20px", fontWeight: "600" }}>Gestión de Usuarios</h2>
        {pendientes > 0 && (
          <span style={{ background: C.red, color: "white", borderRadius: "20px", padding: "2px 10px", fontSize: "12px", fontWeight: "700" }}>
            {pendientes} pendiente{pendientes > 1 ? "s" : ""}
          </span>
        )}
        {sesiones.length > 0 && tab !== "activos" && (
          <span style={{ background: "#15803d", color: "white", borderRadius: "20px", padding: "2px 10px", fontSize: "12px", fontWeight: "700", marginLeft: "4px" }}>
            🟢 {sesiones.length} en línea
          </span>
        )}
      </div>

      {/* Mensaje */}
      {mensaje && (
        <div style={{ marginBottom: "16px", padding: "12px 16px", borderRadius: "8px",
          background: tipoMsg === "ok" ? C.greenBg : "#fef2f2",
          border: `1px solid ${tipoMsg === "ok" ? C.greenBorder : "#fca5a5"}`,
          color: tipoMsg === "ok" ? C.green : "#991b1b", fontSize: "13px", fontWeight: "500" }}>
          {mensaje}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "16px", borderBottom: "2px solid #e2e8f0", paddingBottom: "0", flexWrap: "wrap" }}>
        {[
          { key: "usuarios",  label: "👥 Usuarios",           badge: pendientes > 0 ? pendientes : null },
          { key: "activos",   label: "🟢 En línea ahora",     badge: sesiones.length > 0 ? sesiones.length : null },
          { key: "auditoria", label: "📋 Log de Cambios",     badge: null },
          { key: "historial", label: "📊 Historial Visitas",  badge: null },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "10px 18px", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: "600",
            background: "transparent", borderBottom: tab === t.key ? `2px solid ${C.red}` : "2px solid transparent",
            color: tab === t.key ? C.red : C.slate, marginBottom: "-2px", borderRadius: "6px 6px 0 0",
            display: "flex", alignItems: "center", gap: "6px",
          }}>
            {t.label}
            {t.badge !== null && (
              <span style={{ background: tab === t.key ? C.red : "#64748b", color: "white", borderRadius: "10px", padding: "1px 7px", fontSize: "11px" }}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══ TAB USUARIOS ══ */}
      {tab === "usuarios" && (
        <div style={{ background: C.white, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {[
              { key: "pendiente", label: "Pendientes" },
              { key: "aprobado",  label: "Aprobados"  },
              { key: "rechazado", label: "Rechazados" },
              { key: "todos",     label: "Todos"      },
            ].map(f => (
              <button key={f.key} onClick={() => setFiltro(f.key)}
                style={{ padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "none",
                  background: filtro === f.key ? C.red : "#f1f5f9", color: filtro === f.key ? "white" : C.slate }}>
                {f.label}
                {f.key === "pendiente" && pendientes > 0 && (
                  <span style={{ marginLeft: "6px", background: "rgba(255,255,255,0.3)", borderRadius: "10px", padding: "1px 6px" }}>{pendientes}</span>
                )}
              </button>
            ))}
            <button onClick={cargarUsuarios}
              style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid #e2e8f0", background: "white", color: C.slate }}>
              🔄 Actualizar
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.slate }}>Cargando usuarios...</div>
          ) : usuariosFiltrados.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.slate }}>No hay usuarios {filtro === "todos" ? "" : `con estado "${filtro}"`}.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Nombre</th>
                    <th style={thStyle}>Cédula</th>
                    <th style={thStyle}>Teléfono</th>
                    <th style={thStyle}>Correo</th>
                    <th style={thStyle}>Aliado</th>
                    <th style={thStyle}>Ciudad</th>
                    <th style={thStyle}>Estado</th>
                    <th style={thStyle}>Permisos</th>
                    <th style={thStyle}>Fecha</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosFiltrados.map((u, i) => {
                    const est = ESTADO_COLORS[u.estado] || ESTADO_COLORS.pendiente;
                    const fecha = u.created_at ? new Date(u.created_at).toLocaleDateString("es-CO") : "-";
                    const rolColor = ROL_COLORS[u.rol] || { bg: "#f1f5f9", color: C.slate };
                    return (
                      <tr key={u.id} style={{ background: i % 2 === 0 ? "white" : "#f8fafc" }}>
                        <td style={tdStyle}><div style={{ fontWeight: "600" }}>{u.nombre} {u.apellido}</div></td>
                        <td style={tdStyle}>{u.cedula}</td>
                        <td style={tdStyle}>{u.telefono}</td>
                        <td style={tdStyle}>{u.correo}</td>
                        <td style={tdStyle}>{u.aliado || "-"}</td>
                        <td style={tdStyle}>{u.ciudad || "-"}</td>
                        <td style={tdStyle}>
                          <span style={{ background: est.bg, color: est.color, border: `1px solid ${est.border}`, borderRadius: "20px", padding: "2px 10px", fontSize: "11px", fontWeight: "700", textTransform: "capitalize" }}>
                            {u.estado}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {(u.rol || "").toLowerCase() === "administrador" ? (
                            <span style={{ background: "#fef2f2", color: "#991b1b", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>👑 Admin</span>
                          ) : u.estado === "aprobado" ? (
                            <button
                              onClick={() => abrirModalPermisos(u)}
                              title="Clic para elegir en qué módulos puede editar"
                              style={{
                                background: Array.isArray(u.modulos_edicion) ? "#fff7ed" : u.puede_editar ? "#f0fdf4" : "#f0f9ff",
                                color: Array.isArray(u.modulos_edicion) ? "#c2410c" : u.puede_editar ? "#15803d" : "#0369a1",
                                border: `1px solid ${Array.isArray(u.modulos_edicion) ? "#fdba74" : u.puede_editar ? "#86efac" : "#bae6fd"}`,
                                borderRadius: "6px", padding: "3px 10px",
                                fontSize: "11px", fontWeight: "600", cursor: "pointer",
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.75"; e.currentTarget.style.transform = "scale(1.05)"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "scale(1)"; }}
                            >
                              {Array.isArray(u.modulos_edicion)
                                ? `🎛️ Editor (${u.modulos_edicion.length} módulo${u.modulos_edicion.length === 1 ? "" : "s"})`
                                : u.puede_editar ? "✏️ Editor (todo)" : "👁️ Visualizador"}
                            </button>
                          ) : (
                            <span style={{ background: "#f1f5f9", color: "#94a3b8", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>
                              {u.puede_editar ? "✏️ Editor" : "👁️ Visualizador"}
                            </span>
                          )}
                        </td>
                        <td style={tdStyle}>{fecha}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {u.estado === "pendiente" && (
                            <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                              <button onClick={() => aprobar(u.id, u.nombre + " " + u.apellido)} disabled={procesando === u.id}
                                style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: procesando === u.id ? "not-allowed" : "pointer", border: "none", background: "#15803d", color: "white" }}>
                                {procesando === u.id ? "..." : "✅ Aprobar"}
                              </button>
                              <button onClick={() => rechazar(u.id)} disabled={procesando === u.id}
                                style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: procesando === u.id ? "not-allowed" : "pointer", border: "none", background: C.red, color: "white" }}>
                                {procesando === u.id ? "..." : "❌ Rechazar"}
                              </button>
                            </div>
                          )}
                          {u.estado === "aprobado" && (
                            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", justifyContent:"center" }}>
                              <button
                                onClick={() => abrirModalReset(u.id, u.nombre + " " + u.apellido)}
                                style={{ padding:"5px 10px", borderRadius:"6px", fontSize:"11px", fontWeight:"700", cursor:"pointer", border:"1px solid #bae6fd", background:"#f0f9ff", color:"#0369a1" }}>
                                🔑 Reset clave
                              </button>
                              <button onClick={() => rechazar(u.id)} disabled={procesando === u.id}
                                style={{ padding: "5px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: procesando === u.id ? "not-allowed" : "pointer", border: "1px solid #fca5a5", background: "#fef2f2", color: "#991b1b" }}>
                                Revocar
                              </button>
                            </div>
                          )}
                          {u.estado === "rechazado" && (
                            <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                              <button onClick={() => aprobar(u.id, u.nombre + " " + u.apellido)} disabled={procesando === u.id}
                                style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: procesando === u.id ? "not-allowed" : "pointer", border: "1px solid #86efac", background: "#f0fdf4", color: "#15803d" }}>
                                Aprobar
                              </button>
                              <button onClick={() => eliminarUsuario(u.id, u.nombre + " " + u.apellido)} disabled={procesando === u.id}
                                title="Elimina el registro por completo, para poder volver a usar esa cédula/teléfono/correo"
                                style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: procesando === u.id ? "not-allowed" : "pointer", border: "1px solid #fca5a5", background: "#fef2f2", color: "#991b1b" }}>
                                🗑️ Eliminar
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", fontSize: "12px", color: C.light }}>
            Total: {usuariosFiltrados.length} usuario{usuariosFiltrados.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

      {/* ══ TAB SESIONES ACTIVAS ══ */}
      {tab === "activos" && (
        <div style={{ background: C.white, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: "13px", color: C.slate }}>
              🔄 Se actualiza automáticamente cada 30 segundos
            </div>
            <button onClick={cargarSesiones}
              style={{ padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid #e2e8f0", background: "white", color: C.slate }}>
              🔄 Actualizar ahora
            </button>
          </div>

          {loadingSes ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.slate }}>Cargando sesiones...</div>
          ) : sesiones.length === 0 ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>👻</div>
              <div style={{ color: C.slate, fontSize: "15px", fontWeight: "600" }}>Nadie conectado en este momento</div>
              <div style={{ color: C.light, fontSize: "13px", marginTop: "6px" }}>Los usuarios aparecerán aquí cuando inicien sesión</div>
            </div>
          ) : (
            <>
              {/* KPI */}
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "10px", padding: "10px 20px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "24px" }}>🟢</span>
                  <div>
                    <div style={{ fontSize: "10px", color: "#15803d", fontWeight: "700", textTransform: "uppercase" }}>Usuarios en línea</div>
                    <div style={{ fontSize: "28px", fontWeight: "800", color: "#15803d", lineHeight: 1 }}>{sesiones.length}</div>
                  </div>
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Estado</th>
                      <th style={thStyle}>Nombre</th>
                      <th style={thStyle}>Cédula</th>
                      <th style={thStyle}>Rol</th>
                      <th style={thStyle}>Página actual</th>
                      <th style={thStyle}>Última actividad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sesiones.map((s, i) => {
                      const rolColor = ROL_COLORS[s.rol] || { bg: "#f1f5f9", color: C.slate };
                      return (
                        <tr key={s.cedula} style={{ background: i % 2 === 0 ? "white" : "#f8fafc" }}>
                          <td style={tdStyle}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: "#15803d", boxShadow: "0 0 0 3px rgba(21,128,61,0.2)" }} />
                              <span style={{ fontSize: "11px", color: "#15803d", fontWeight: "700" }}>En línea</span>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: "600" }}>{s.nombre}</td>
                          <td style={tdStyle}>{s.cedula}</td>
                          <td style={tdStyle}>
                            <span style={{ background: rolColor.bg, color: rolColor.color, borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>
                              {s.rol}
                            </span>
                          </td>
                          <td style={tdStyle}>{paginaLabel(s.pagina)}</td>
                          <td style={tdStyle}>{formatFecha(s.ultima_actividad)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ TAB AUDITORÍA ══ */}
      {tab === "auditoria" && (
        <div style={{ background: C.white, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <input type="text" placeholder="Filtrar por cédula..." value={filtroCedAud}
              onChange={e => setFiltroCedAud(e.target.value)}
              style={{ padding: "7px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px", color: C.navy, background: "#f8fafc", outline: "none", width: "200px" }}
              onKeyDown={e => { if (e.key === "Enter") cargarAuditoria(); }} />
            <button onClick={cargarAuditoria}
              style={{ padding: "7px 16px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "none", background: C.red, color: "white" }}>
              Buscar
            </button>
            {filtroCedAud && (
              <button onClick={() => { setFiltroCedAud(""); }}
                style={{ padding: "7px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid #e2e8f0", background: "white", color: C.slate }}>
                ✕ Limpiar
              </button>
            )}
            <div style={{ marginLeft: "auto", fontSize: "12px", color: C.light }}>
              Últimos 200 cambios
            </div>
          </div>

          {loadingAud ? (
            <div style={{ padding: "40px", textAlign: "center", color: C.slate }}>Cargando auditoría...</div>
          ) : auditoria.length === 0 ? (
            <div style={{ padding: "48px", textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "12px" }}>📋</div>
              <div style={{ color: C.slate, fontSize: "15px", fontWeight: "600" }}>No hay cambios registrados</div>
              <div style={{ color: C.light, fontSize: "13px", marginTop: "6px" }}>Los cambios en datos editables aparecerán aquí</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Fecha / Hora</th>
                    <th style={thStyle}>Usuario</th>
                    <th style={thStyle}>Cédula</th>
                    <th style={thStyle}>Rol</th>
                    <th style={thStyle}>Nodo</th>
                    <th style={thStyle}>Campo</th>
                    <th style={thStyle}>Valor anterior</th>
                    <th style={thStyle}>Valor nuevo</th>
                  </tr>
                </thead>
                <tbody>
                  {auditoria.map((a, i) => {
                    const rolColor = ROL_COLORS[a.rol] || { bg: "#f1f5f9", color: C.slate };
                    const campoColor = {
                      "causa_falla":              { bg: "#fef2f2", color: "#991b1b" },
                      "causa_incumplimiento_icr": { bg: "#eff6ff", color: "#1d4ed8" },
                      "observaciones":            { bg: "#f0fdf4", color: "#15803d" },
                    }[a.campo_modificado] || { bg: "#f1f5f9", color: C.slate };

                    return (
                      <tr key={a.id} style={{ background: i % 2 === 0 ? "white" : "#f8fafc" }}>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap", color: C.slate }}>{formatFecha(a.fecha)}</td>
                        <td style={{ ...tdStyle, fontWeight: "600" }}>{a.nombre_usuario || "—"}</td>
                        <td style={tdStyle}>{a.cedula || "—"}</td>
                        <td style={tdStyle}>
                          <span style={{ background: rolColor.bg, color: rolColor.color, borderRadius: "6px", padding: "2px 7px", fontSize: "11px", fontWeight: "600" }}>
                            {a.rol || "—"}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>{a.id_registro}</td>
                        <td style={tdStyle}>
                          <span style={{ background: campoColor.bg, color: campoColor.color, borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>
                            {a.campo_modificado}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, color: "#991b1b", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.valor_anterior || <span style={{ color: C.light, fontStyle: "italic" }}>vacío</span>}
                        </td>
                        <td style={{ ...tdStyle, color: "#15803d", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.valor_nuevo || <span style={{ color: C.light, fontStyle: "italic" }}>vacío</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", fontSize: "12px", color: C.light }}>
            {auditoria.length} registro{auditoria.length !== 1 ? "s" : ""} encontrado{auditoria.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

      {/* ══ TAB HISTORIAL DE VISITAS — NUEVO ══ */}
      {tab === "historial" && (
        <div>
          {/* Filtros */}
          <div style={{ background: C.white, borderRadius: "12px", border: "1px solid #e2e8f0", padding: "16px 20px", marginBottom: "12px", display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label style={{ fontSize: "11px", color: C.slate, fontWeight: "600", display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Cédula</label>
              <input
                value={histFiltros.cedula}
                onChange={e => setHistFiltros(p => ({ ...p, cedula: e.target.value }))}
                placeholder="Cédula..."
                style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", width: "170px", color: C.navy }}
              />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: C.slate, fontWeight: "600", display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Desde</label>
              <input
                type="date"
                value={histFiltros.desde}
                onChange={e => setHistFiltros(p => ({ ...p, desde: e.target.value }))}
                style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", color: C.navy }}
              />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: C.slate, fontWeight: "600", display: "block", marginBottom: "4px", textTransform: "uppercase" }}>Hasta</label>
              <input
                type="date"
                value={histFiltros.hasta}
                onChange={e => setHistFiltros(p => ({ ...p, hasta: e.target.value }))}
                style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", color: C.navy }}
              />
            </div>
            <button
              onClick={cargarHistorial}
              style={{ padding: "8px 20px", background: C.red, color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
              🔍 Buscar
            </button>
            <button
              onClick={() => setHistFiltros({ cedula: "", desde: "", hasta: "" })}
              style={{ padding: "8px 14px", background: "#f1f5f9", color: C.slate, border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", cursor: "pointer" }}>
              ✖ Limpiar
            </button>
            <div style={{ marginLeft: "auto", fontSize: "12px", color: C.light, alignSelf: "center" }}>
              Últimas 500 visitas
            </div>
          </div>

          {/* Tabla */}
          <div style={{ background: C.white, borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            {loadingHist ? (
              <div style={{ padding: "48px", textAlign: "center", color: C.slate }}>Cargando historial...</div>
            ) : historial.length === 0 ? (
              <div style={{ padding: "48px", textAlign: "center" }}>
                <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔍</div>
                <div style={{ color: C.slate, fontSize: "15px", fontWeight: "600" }}>No hay registros</div>
                <div style={{ color: C.light, fontSize: "13px", marginTop: "6px" }}>
                  Ajusta los filtros o espera a que los usuarios naveguen por el sitio
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Fecha y hora</th>
                      <th style={thStyle}>Nombre</th>
                      <th style={thStyle}>Cédula</th>
                      <th style={thStyle}>Rol</th>
                      <th style={thStyle}>Página visitada</th>

                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((h, i) => {
                      const rolColor = ROL_COLORS[h.rol] || { bg: "#f1f5f9", color: C.slate };
                      return (
                        <tr key={h.id} style={{ background: i % 2 === 0 ? "white" : "#f8fafc" }}>
                          <td style={{ ...tdStyle, whiteSpace: "nowrap", color: C.slate }}>{formatFecha(h.fecha)}</td>
                          <td style={{ ...tdStyle, fontWeight: "600" }}>{h.nombre}</td>
                          <td style={{ ...tdStyle, fontFamily: "monospace", color: C.slate }}>{h.cedula}</td>
                          <td style={tdStyle}>
                            <span style={{ background: rolColor.bg, color: rolColor.color, borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>
                              {h.rol}
                            </span>
                          </td>
                          <td style={tdStyle}>{paginaLabel(h.pagina)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9", fontSize: "12px", color: C.light }}>
                  Total: {historial.length} registro{historial.length !== 1 ? "s" : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    {/* ══ MODAL RESET CONTRASEÑA ══ */}
    {modalReset && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
        <div style={{ background:"white", borderRadius:"16px", padding:"32px", width:"100%", maxWidth:"400px", margin:"20px", boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" }}>
            <div>
              <h3 style={{ margin:"0 0 4px 0", color:C.navy, fontSize:"18px", fontWeight:"700" }}>🔑 Resetear contraseña</h3>
              <p style={{ margin:0, color:C.slate, fontSize:"12px" }}>{modalReset.nombre}</p>
            </div>
            <button onClick={() => setModalReset(null)} style={{ background:"none", border:"none", fontSize:"18px", cursor:"pointer", color:C.slate }}>✕</button>
          </div>

          <div style={{ marginBottom:"16px" }}>
            <label style={{ display:"block", fontSize:"11px", fontWeight:"700", color:C.slate, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"6px" }}>
              Nueva contraseña temporal
            </label>
            <input
              type="text"
              value={passReset}
              onChange={e => { setPassReset(e.target.value); setResetError(""); }}
              placeholder="Mínimo 6 caracteres"
              style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", border:"2px solid #e2e8f0", borderRadius:"10px", fontSize:"13px", color:C.navy, background:"#f8fafc", outline:"none" }}
              onFocus={e => e.target.style.borderColor = C.red}
              onBlur={e => e.target.style.borderColor = "#e2e8f0"}
            />
            <p style={{ margin:"6px 0 0 0", fontSize:"11px", color:C.slate }}>
              💡 Comparte esta clave con el usuario para que pueda ingresar y cambiarla desde su perfil.
            </p>
          </div>

          {resetError && <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:"8px", padding:"10px 14px", color:"#991b1b", fontSize:"13px", marginBottom:"12px" }}>{resetError}</div>}
          {resetExito && <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:"8px", padding:"10px 14px", color:"#15803d", fontSize:"13px", marginBottom:"12px" }}>{resetExito}</div>}

          <div style={{ display:"flex", gap:"10px" }}>
            <button onClick={() => setModalReset(null)}
              style={{ flex:1, padding:"11px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:"10px", fontSize:"13px", fontWeight:"600", cursor:"pointer", color:C.slate }}>
              Cancelar
            </button>
            <button onClick={confirmarReset} disabled={resetLoading}
              style={{ flex:1, padding:"11px", background:resetLoading ? "#9ca3af" : "linear-gradient(135deg, #0369a1, #0284c7)", color:"white", border:"none", borderRadius:"10px", fontSize:"13px", fontWeight:"700", cursor:resetLoading ? "not-allowed" : "pointer" }}>
              {resetLoading ? "Guardando..." : "🔑 Resetear"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ══ MODAL APROBAR CON PERMISOS ══ */}
    {modalAprobar && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
        <div style={{ background:"white", borderRadius:"16px", padding:"32px", width:"100%", maxWidth:"420px", margin:"20px", boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
          <div style={{ marginBottom:"20px" }}>
            <h3 style={{ margin:"0 0 6px 0", color:C.navy, fontSize:"18px", fontWeight:"700" }}>Aprobar usuario</h3>
            <p style={{ margin:0, color:C.slate, fontSize:"13px" }}>{modalAprobar.nombre}</p>
          </div>

          <div style={{ marginBottom:"24px" }}>
            <label style={{ display:"block", fontSize:"11px", fontWeight:"700", color:C.slate, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"12px" }}>
              Tipo de acceso
            </label>
            <SelectorPermisoModulos
              modo={modoPermiso}
              setModo={setModoPermiso}
              modulosSeleccionados={modulosSeleccionados}
              onToggleModulo={alternarModuloSeleccionado}
            />
          </div>

          <div style={{ display:"flex", gap:"10px" }}>
            <button onClick={() => setModalAprobar(null)}
              style={{ flex:1, padding:"11px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:"10px", fontSize:"13px", fontWeight:"600", cursor:"pointer", color:C.slate }}>
              Cancelar
            </button>
            <button onClick={confirmarAprobacion} disabled={procesando === modalAprobar?.id}
              style={{ flex:1, padding:"11px", background:"linear-gradient(135deg, #15803d, #166534)", color:"white", border:"none", borderRadius:"10px", fontSize:"13px", fontWeight:"700", cursor:"pointer" }}>
              {procesando === modalAprobar?.id ? "Aprobando..." : "✅ Confirmar aprobación"}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ══ MODAL PERMISOS (usuario ya aprobado) ══ */}
    {modalPermisos && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
        <div style={{ background:"white", borderRadius:"16px", padding:"32px", width:"100%", maxWidth:"420px", margin:"20px", boxShadow:"0 24px 80px rgba(0,0,0,0.4)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"20px" }}>
            <div>
              <h3 style={{ margin:"0 0 6px 0", color:C.navy, fontSize:"18px", fontWeight:"700" }}>Permisos de edición</h3>
              <p style={{ margin:0, color:C.slate, fontSize:"13px" }}>{modalPermisos.nombre}</p>
            </div>
            <button onClick={() => setModalPermisos(null)} style={{ background:"none", border:"none", fontSize:"18px", cursor:"pointer", color:C.slate }}>✕</button>
          </div>

          <div style={{ marginBottom:"24px" }}>
            <label style={{ display:"block", fontSize:"11px", fontWeight:"700", color:C.slate, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"12px" }}>
              Tipo de acceso
            </label>
            <SelectorPermisoModulos
              modo={modoPermiso}
              setModo={setModoPermiso}
              modulosSeleccionados={modulosSeleccionados}
              onToggleModulo={alternarModuloSeleccionado}
            />
          </div>

          <div style={{ display:"flex", gap:"10px" }}>
            <button onClick={() => setModalPermisos(null)}
              style={{ flex:1, padding:"11px", background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:"10px", fontSize:"13px", fontWeight:"600", cursor:"pointer", color:C.slate }}>
              Cancelar
            </button>
            <button onClick={confirmarPermisos} disabled={guardandoPermisos}
              style={{ flex:1, padding:"11px", background: guardandoPermisos ? "#9ca3af" : "linear-gradient(135deg, #15803d, #166534)", color:"white", border:"none", borderRadius:"10px", fontSize:"13px", fontWeight:"700", cursor: guardandoPermisos ? "not-allowed" : "pointer" }}>
              {guardandoPermisos ? "Guardando..." : "💾 Guardar permisos"}
            </button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
}